import {
  Box,
  Button,
  Center,
  Checkbox,
  CheckboxGroup,
  FormControl,
  FormLabel,
  Heading,
  Image,
  Radio,
  RadioGroup,
  Stack,
  Text
} from '@chakra-ui/react'
import { Chart, registerables } from 'chart.js'
import { useEffect, useState } from 'react'

import { Icon } from '../../../components/Icon'
import { LoadingBlock } from '../../../components/Loading'
import { StarRating } from '../../../components/StarRating'
import { formatApiDate } from '../../../helpers'
import { IBrilhaMagentaAnswer } from '../../../interfaces/BrilhaMagenta/IBrilhaMagentaAnswer'
import BrilhaMagentaService from '../../../services/BrilhaMagenta/BrilhaMagentaService'

Chart.register(...registerables)

interface IFormField {
  id: number
  title: string
  type: string
  options: string
  groups: string
}

export default function BrilhaMagentaViewPage({
  brilhaMagentaId: id,
  skipLastStep
}: {
  brilhaMagentaId: number
  skipLastStep: boolean
}) {
  const brilhaMagentaService = new BrilhaMagentaService()
  const [form, setForm] = useState<IFormField[]>([])
  const [formTitle, setFormTitle] = useState<string>('')
  const [formAnswers, setFormAnswers] = useState<IBrilhaMagentaAnswer>({})
  const [step, setStep] = useState<number>(0)
  const [steps, setSteps] = useState<number[]>([])
  const [loading, setLoading] = useState<boolean>(false)

  const loadFormPage = async () => {
    setLoading(true)
    brilhaMagentaService
      .getResponses(id, step)
      .then((response) => {
        setFormAnswers({})
        setForm([])

        const answers: IBrilhaMagentaAnswer = {}
        response.data.answers.forEach((answer: { question_id: number; answer: string }) => {
          answers[answer.question_id] = answer.answer
        })
        setFormAnswers(answers)

        setFormTitle(response.data.form.name)
        setForm(response.data.questions)
      })
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    window.scrollTo(0, 0)

    loadFormPage()
  }, [step])

  const getSteps = () => {
    brilhaMagentaService.getSteps().then((response) => {
      setSteps(response.data)
      setStep(response.data[0])
    })
  }

  useEffect(() => {
    getSteps()
  }, [])

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
  }

  return (
    <>
      <Center>
        <Image src="/img/brilha-magenta.png" alt="Brilha Magenta" w="65%" marginBottom="10" />
      </Center>
      {loading && <LoadingBlock size="page" />}
      {!loading && (
        <>
          <Heading as="h2" mb="4">
            {formTitle}
          </Heading>
          <form onSubmit={handleSubmit}>
            {form.map((field) => {
              return (
                <FormControl key={field.id} mb="4">
                  <FormLabel>{field.title}</FormLabel>
                  {field.type === 'textarea' &&
                    (formAnswers[field.id] ?? '').split('\n').map((line, index) => <Text key={index}>{line}</Text>)}
                  {field.type === 'text' &&
                    (formAnswers[field.id] ?? '').split('\n').map((line, index) => <Text key={index}>{line}</Text>)}
                  {field.type === 'date' && formatApiDate(formAnswers[field.id])}
                  {field.type === 'options' && (
                    <RadioGroup value={formAnswers[field.id]}>
                      <Stack direction="column" spacing={3}>
                        {(JSON.parse(field.options) as string[]).map((option, index) => (
                          <Radio value={(index + 1).toString()} key={index} isDisabled>
                            {step !== 2 && step !== 4 && option}
                            {step === 2 && (
                              <>
                                {JSON.parse(field.groups)[index] === 4 && (
                                  <Text color="green" fontWeight="semibold">
                                    {option}
                                  </Text>
                                )}
                                {JSON.parse(field.groups)[index] !== 4 &&
                                  ((formAnswers[field.id] == (index + 1).toString() &&
                                    JSON.parse(field.groups)[parseInt(formAnswers[field.id]) - 1] != 4 && (
                                      <Text color="tomato" fontWeight="semibold">
                                        {option}
                                      </Text>
                                    )) ||
                                    option)}
                              </>
                            )}
                          </Radio>
                        ))}
                      </Stack>
                    </RadioGroup>
                  )}
                  {field.type === 'checkbox' && (
                    <CheckboxGroup value={formAnswers[field.id] ? JSON.parse(formAnswers[field.id]) : []}>
                      {Array.isArray(JSON.parse(field.options)) ? (
                        (JSON.parse(field.options) as string[]).map((option, index) => (
                          <Checkbox value={(index + 1).toString()} key={index} marginLeft="2">
                            {option}
                          </Checkbox>
                        ))
                      ) : (
                        <Checkbox value={(1).toString()} marginLeft="2">
                          {JSON.parse(field.options)}
                        </Checkbox>
                      )}
                    </CheckboxGroup>
                  )}
                  {field.type === 'stars' && <StarRating value={parseInt(formAnswers[field.id] ?? '0')} />}
                </FormControl>
              )
            })}

            <Box display={['block', 'block', 'none']}>
              <br />
              <Button
                colorScheme="magenta"
                type="button"
                onClick={() => setStep(steps[steps.indexOf(step) - 1])}
                float="left"
                disabled={step <= steps[0]}
              >
                <Icon name="lni lni-arrow-left" />
                &nbsp;Passo anterior
              </Button>
              <Button
                colorScheme="magenta"
                type="button"
                onClick={() => setStep(steps[steps.indexOf(step) + 1])}
                float="right"
                disabled={skipLastStep ? step >= steps[steps.length - 2] : step >= steps[steps.length - 1]}
              >
                Próximo passo &nbsp;
                <Icon name="lni lni-arrow-right" />
              </Button>
            </Box>

            <Box display={['none', 'none', 'block']}>
              <Button
                colorScheme="blackAlpha"
                opacity="90%"
                type="button"
                onClick={() => setStep(steps[steps.indexOf(step) - 1])}
                position={'fixed'}
                left={'5rem'}
                bottom={'50%'}
                fontSize={'xxx-large'}
                padding={'30px 5px'}
                textAlign={'center'}
                disabled={step <= steps[0]}
              >
                <Icon name="lni lni-chevron-left" />
              </Button>
              <Button
                colorScheme="blackAlpha"
                opacity="90%"
                type="button"
                onClick={() => setStep(steps[steps.indexOf(step) + 1])}
                position={'fixed'}
                right={'6rem'}
                bottom={'50%'}
                fontSize={'xxx-large'}
                padding={'30px 5px'}
                textAlign={'center'}
                disabled={steps.indexOf(step) >= steps.length - (skipLastStep ? 2 : 1)}
              >
                <Icon name="lni lni-chevron-right" />
              </Button>
            </Box>
          </form>
        </>
      )}
    </>
  )
}
